/**
 * Hero with animated counters and the typed code snippet.
 *
 * Ported from `public/index.html:64-123`. The counter targets and suffixes come
 * from the `data-target` / `data-suffix` attributes, and the three snippet lines
 * from the `data-text` attributes.
 */
import { useMemo } from 'react';

import type { CountUpStat } from '../../hooks/useCountUp';
import { useCountUp } from '../../hooks/useCountUp';
import type { TypedLine } from '../../hooks/useTypedSequence';
import { useTypedSequence } from '../../hooks/useTypedSequence';

const STATS: CountUpStat[] = [
  { target: 2.8, suffix: 'M+', label: 'Cells' },
  { target: 470, suffix: '+', label: 'Donors' },
  { target: 40, suffix: '+', label: 'Studies' },
  { target: 15, suffix: '+', label: 'Diseases' }
];

const SNIPPET: TypedLine[] = [
  {
    text: 'Compare AT2 cell markers across BPD and control',
    speed: 30,
    delay: 0
  },
  { text: 'Analyzing 2.8M+ cells across datasets...', speed: 40, delay: 800 },
  { text: 'Generated heatmap + pathway enrichment', speed: 30, delay: 500 }
];

interface Props {
  onNavigate: (hash: string) => void;
}

const HeroSection = ({ onNavigate }: Props) => {
  const stats = useMemo(() => STATS, []);
  const snippet = useMemo(() => SNIPPET, []);
  const counters = useCountUp(stats);
  const typed = useTypedSequence(snippet);

  const setRefs = (node: HTMLElement | null) => {
    counters.ref.current = node;
    typed.ref.current = node;
  };

  const prefix = ['User:', 'LungChat:', '✓'];
  const prefixClass = ['code-user', 'code-agent', 'code-result'];

  return (
    <section className="hero" ref={setRefs}>
      <h1 className="hero-title">
        Ask Questions, Get Insights
        <br />
        <span className="gradient-text">Conversational Lung Biology AI</span>
      </h1>
      <p className="hero-subtitle">
        Perform complex multiomics analyses through natural language. LungChat
        coordinates specialist agents for single-cell RNA-seq, spatial
        transcriptomics, pathway enrichment, DART drug repurposing and safety
        profiling, and literature research. Accelerate discovery with
        publication-ready figures, tables, and parameter provenance.
      </p>

      <div className="hero-stats">
        {stats.map((stat, index) => (
          <div className="hero-stat" key={stat.label}>
            <span
              className="stat-value"
              data-target={stat.target}
              data-suffix={stat.suffix}
            >
              {counters.values[index]}
            </span>
            <span className="stat-label">{stat.label}</span>
          </div>
        ))}
      </div>

      <div className="cta-buttons">
        <a href="/lungchat" className="btn-primary">
          Try LungChat
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M5 12h14M12 5l7 7-7 7" />
          </svg>
        </a>
        <a
          href="#about"
          className="btn-secondary"
          onClick={(event) => {
            event.preventDefault();
            onNavigate('#about');
          }}
        >
          Learn More
        </a>
        <a
          href="/paper/iclr-2026"
          className="btn-secondary"
          target="_blank"
          rel="noopener"
        >
          Read the Paper
        </a>
      </div>

      <div className="hero-visual">
        <div className="visual-card">
          <div className="code-snippet">
            {snippet.map((line, index) => (
              <span
                className="code-line"
                key={line.text}
                style={{
                  display: typed.lines[index].visible ? 'flex' : 'none'
                }}
              >
                <span className={prefixClass[index]}>{prefix[index]}</span>{' '}
                <span className="typing-text" data-text={line.text}>
                  {typed.lines[index].typed}
                </span>
                {index < 2 ? (
                  <span
                    className="typing-cursor"
                    style={{
                      display: typed.lines[index].done ? 'none' : undefined
                    }}
                  >
                    |
                  </span>
                ) : null}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
